import { Box, Typography } from "@mui/material";
import moment from "moment";
import { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "react-toastify";

import { ActionButton, CardLayout, CrudTable, InputField, Title } from "../v3";

import { PurchaseReturnItemColumns } from "../../pages/vendor/constants"; // we define below
import { PurchaseService } from "../../services";
import { markTableReset } from "../../utils/tableStateStorage";

export const PurchaseReturn = ({ purchaseInvoiceId }) => {
  const navigate = useNavigate();

  const [loading, setLoading] = useState(false);
  const [returnDate, setReturnDate] = useState(moment().format("YYYY-MM-DD"));

  const [invoiceItems, setInvoiceItems] = useState([]);
  const [returnItems, setReturnItems] = useState([]);

  const availableInvoiceItems = useMemo(() => {
    const selectedIds = new Set(returnItems.map((item) => item.purchaseItemId));

    return invoiceItems.filter((item) => !selectedIds.has(item.purchaseItemId));
  }, [invoiceItems, returnItems]);

  /* =====================================================
     FETCH INVOICE ITEMS
  ===================================================== */

  useEffect(() => {
    if (purchaseInvoiceId) {
      fetchInvoiceItems(purchaseInvoiceId);
    }
  }, [purchaseInvoiceId]);

  const fetchInvoiceItems = async (id) => {
    try {
      setLoading(true);

      const { data: purchaseInvoiceData } = await PurchaseService.getPurchaseById(id);

      const purchaseItemIds = Array.from(new Set(purchaseInvoiceData?.items?.map((item) => item.purchaseItemId)));

      const { data: purchaseItems } = await PurchaseService.getPurchaseItems({
        ids: purchaseItemIds.join(","),
      });

      // ✅ Create fast lookup map
      const purchaseItemMap = new Map(purchaseItems?.rows?.map((pi) => [pi.id, pi]));

      const items =
        purchaseInvoiceData?.items?.map((item) => {
          const material = purchaseItemMap.get(item.purchaseItemId);

          return {
            id: item.purchaseItemId,
            purchaseItemId: item.purchaseItemId,
            name: material?.name,
            label: material?.name,
            value: item.purchaseItemId,
            quantity: Number(item.quantity),
            price: item.price,
            taxPercentage: item.taxPercentage,
            unitOfMeasure: item.unitOfMeasure,
          };
        }) || [];

      setInvoiceItems(items);
    } catch (error) {
      console.error(error);
      toast.error("Failed to load invoice items");
    } finally {
      setLoading(false);
    }
  };

  /* =====================================================
     HANDLE SELECT CHANGE
  ===================================================== */

  const handleItemSelect = (event) => {
    const selectedId = Number(event.target.value);

    if (!selectedId) return;

    const selectedItem = invoiceItems.find((item) => item.purchaseItemId === selectedId);

    if (!selectedItem) return;

    setReturnItems((prev) => {
      // Prevent duplicate entries
      if (prev.some((item) => item.purchaseItemId === selectedId)) {
        return prev;
      }

      return [
        ...prev,
        {
          id: selectedItem.purchaseItemId,
          purchaseItemId: selectedItem.purchaseItemId,
          name: selectedItem.name,
          originalQuantity: selectedItem.quantity,
          returnQuantity: 1,
          price: selectedItem.price,
          taxPercentage: selectedItem.taxPercentage,
          unitOfMeasure: selectedItem.unitOfMeasure,
        },
      ];
    });
  };

  /* =====================================================
     DERIVED TOTAL
  ===================================================== */

  const totalReturnAmount = useMemo(() => {
    return returnItems
      .reduce((total, item) => {
        const qty = Number(item.returnQuantity || 0);
        const price = Number(item.price || 0);
        const tax = Number(item.taxPercentage || 0);

        const line = qty * price;
        return total + line + (tax / 100) * line;
      }, 0)
      .toFixed(2);
  }, [returnItems]);

  /* =====================================================
     SAVE HANDLER
  ===================================================== */

  const handleSave = async () => {
    const validItems = returnItems.filter((item) => Number(item.returnQuantity) > 0);

    if (!validItems.length) {
      toast.error("Enter return quantity for at least one item");
      return;
    }

    try {
      setLoading(true);

      const now = moment();
      const dateWithCurrentTime = moment(returnDate).set({
        hour: now.hour(),
        minute: now.minute(),
        second: now.second(),
      });

      const payload = {
        purchaseInvoiceId: Number(purchaseInvoiceId),
        returnDate: dateWithCurrentTime.toISOString(),
        items: validItems.map((item) => ({
          purchaseItemId: item.purchaseItemId,
          quantity: Number(item.returnQuantity),
        })),
      };

      await PurchaseService.createPurchaseReturn(payload);

      toast.success("Purchase return created successfully");
      // The list opens fresh, so its filters don't hide the new return.
      markTableReset();
      navigate(-1);
    } catch (error) {
      console.error(error);
      toast.error("Failed to create return");
    } finally {
      setLoading(false);
    }
  };

  return (
    <>
      {/* HEADER */}
      <CardLayout>
        <Box display="flex" gap={2}>
          <InputField
            type="date"
            label="Return Date"
            value={returnDate}
            onChange={(e) => setReturnDate(e.target.value)}
            style={{ width: "100%" }}
          />
        </Box>
      </CardLayout>

      {/* ITEMS SECTION */}
      <CardLayout>
        <Typography variant="body2" sx={{ fontWeight: 600 }}>
          Select Items
        </Typography>

        <Box mt={2}>
          <InputField
            required
            select={true}
            selectOptions={availableInvoiceItems}
            label="Items"
            name="items"
            onChange={handleItemSelect}
            fullWidth
          />
        </Box>

        <CrudTable
          loading={loading}
          rows={returnItems}
          setRows={setReturnItems}
          columns={PurchaseReturnItemColumns()}
          hidePagination
        />

        <Box display="flex" justifyContent="flex-end" mt={2}>
          <Title textVariant="p" title={`Total Return: ${totalReturnAmount}`} style={{ fontWeight: "bold" }} />
        </Box>

        <Box display="flex" justifyContent="flex-end" gap={2} mt={2}>
          <ActionButton buttonText="Cancel" variant="outlined" onButtonClick={() => navigate(-1)} />
          <ActionButton buttonText="Save" color="primary" onButtonClick={handleSave} loading={loading} />
        </Box>
      </CardLayout>
    </>
  );
};
